import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../api";
import { QuarterSelector, QUARTER_PRESETS } from "./QuarterSelector";
import { Skeleton } from "./Skeleton";

interface WalletPointsShareProps {
  walletAddress: string;
}

export function WalletPointsShare({ walletAddress }: WalletPointsShareProps) {
  const defaultPreset = QUARTER_PRESETS[0]!;
  const [period, setPeriod] = useState({
    from: defaultPreset.from,
    to: defaultPreset.to,
    label: defaultPreset.label,
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ["quarterly-points-wallet", period.from, period.to],
    queryFn: async () => {
      const { data, error } = await api.points.quarterly.get({
        query: { from: period.from, to: period.to, limit: 500 },
      });
      if (error) throw error;
      return data;
    },
    enabled: !!period.from && !!period.to,
  });

  const walletData = data?.data.find(
    (w) => w.walletAddress === walletAddress
  );
  const walletRank = data?.data.findIndex(
    (w) => w.walletAddress === walletAddress
  );

  return (
    <div className="space-y-4">
      <QuarterSelector
        onPeriodChange={(from, to, label) => setPeriod({ from, to, label })}
        initialQuarter={QUARTER_PRESETS[0]!.id}
      />

      {isLoading && (
        <Skeleton kind="tiles" rows={4} />
      )}

      {error && (
        <p className="text-red-400 text-sm">
          Could not load share data: {String(error)}
        </p>
      )}

      {data && !isLoading && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="card-inset p-3">
            <div className="text-gray-400 text-xs mb-1">Points gained</div>
            <div className="text-lg font-semibold text-gray-50 num">
              {walletData?.pointsGained.toLocaleString() ?? "0"}
            </div>
          </div>
          <div className="card-inset p-3">
            <div className="text-gray-400 text-xs mb-1">Share of total</div>
            <div className="text-lg font-semibold text-yellow-400 num">
              {walletData?.sharePercent.toFixed(2) ?? "0.00"}%
            </div>
          </div>
          <div className="card-inset p-3">
            <div className="text-gray-400 text-xs mb-1">Rank</div>
            <div className="text-lg font-semibold text-gray-50 num">
              {walletRank !== undefined && walletRank >= 0
                ? `#${walletRank + 1}`
                : "-"}
            </div>
          </div>
          <div className="card-inset p-3">
            <div className="text-gray-400 text-xs mb-1">Platform total</div>
            <div className="text-lg font-semibold text-gray-300 num">
              {data.totalPlatformGain.toLocaleString()}
            </div>
          </div>
        </div>
      )}

      {walletData && (
        <div className="card-inset p-3">
          <div className="flex justify-between text-sm text-gray-400 mb-2">
            <span>Share of platform points ({period.label})</span>
            <span className="text-gray-100 font-medium num">
              {walletData.sharePercent.toFixed(2)}%
            </span>
          </div>
          <div className="w-full bg-gray-700 rounded-full h-2">
            <div
              className="bg-yellow-400 h-2 rounded-full"
              style={{
                width: `${Math.min(walletData.sharePercent, 100)}%`,
              }}
            />
          </div>
          <div className="grid grid-cols-3 gap-2 mt-3 text-xs num">
            <div>
              <span className="text-gray-500">Primary </span>
              <span className="text-gray-200">
                +{walletData.primaryGained.toLocaleString()}
              </span>
            </div>
            <div>
              <span className="text-gray-500">Secondary </span>
              <span className="text-gray-200">
                +{walletData.secondaryGained.toLocaleString()}
              </span>
            </div>
            <div>
              <span className="text-gray-500">Bonus </span>
              <span className="text-gray-200">
                +{walletData.bonusGained.toLocaleString()}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
